import Authenticated from '@/components/Authenticated';
import ErrorBoundary from '@/components/ErrorPage/ErrorBoundary';
import Intercom from '@/components/Intercom';
import Layout from '@/components/Layout';
import NotFound from '@/components/NotFound';
import ZendeskMessenger from '@/components/Zendesk/ZendeskMessenger';
import { loggedInRoutes, notLoggedInRoutes } from '@/routes';
import './sass/app.scss';
import store from '@/store';
import { AccountChecker } from '@/support/AccountChecker';
import { ApiIdMapper } from '@/support/ApiId';
import { BrowserHistoryEventBridge } from '@/support/BrowserHistoryEventBridge.tsx';
import { ExpiredJwtDetector } from '@/support/ExpiredJwtDetector.tsx';
import { NetworkStatusDetector } from '@/support/NetworkStatusDetector.tsx';
import { UpdateChecker } from '@/support/UpdateChecker';
import { AlertContainer } from '@/utils/alerts/AlertContainer';
import { DevicesProvider } from '@/utils/devices/DevicesProvider';
import { EntitySchemaProvider } from '@/utils/entitySchema/EntitySchemaProvider';
import { FollowFocusConsumer } from '@/utils/devices/FollowFocusConsumer';
import { ComponentType, FC, ReactNode, useEffect, useRef, useMemo } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Provider as ReduxProvider, useDispatch } from 'react-redux';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
  NavigateFunction,
} from 'react-router-dom';
import { Bounce, ToastContainer } from 'react-toastify';

/**
 * Closes any open Redux modal when the route pathname changes.
 *
 * In React Router v5, `<Switch>` fully remounted route components on navigation,
 * which naturally tore down modals. With v7's `createBrowserRouter`, the component
 * tree is more stable, so Redux-managed modals persist across route transitions.
 * This bridge restores the v5 behaviour by dispatching CLOSE_MODAL on every
 * pathname change (including browser back/forward).
 */
const RouteChangeModalCloser: FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const previousPathname = useRef(location.pathname);

  useEffect(() => {
    if (previousPathname.current !== location.pathname) {
      dispatch({ type: 'CLOSE_MODAL' });
      previousPathname.current = location.pathname;
    }
  }, [location.pathname, dispatch]);

  return null;
};

type RouteConfig = (typeof loggedInRoutes)[number];
type PublicRouteConfig = (typeof notLoggedInRoutes)[number];

/// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;
interface RouteComponentProps {
  component: AnyComponent;
  page?: string;
  history?: NavigateFunction;
}
/** Type-safe component renderer for dynamic route components */
const RouteComponent: FC<RouteComponentProps> = ({ component: Component, ...props }) => <Component {...props} />;

/**
 * Root layout component that wraps all routes with necessary providers.
 * This is required for createBrowserRouter to work with context providers.
 */
const RootLayout: FC = () => {
  return (
    <ReduxProvider store={store}>
      {/* @ts-expect-error: react-dnd types issue with children prop */}
      <DndProvider backend={HTML5Backend}>
        <BrowserHistoryEventBridge />
        <RouteChangeModalCloser />
        <ErrorBoundary>
          <DevicesProvider>
            <EntitySchemaProvider>
              <FollowFocusConsumer />
              <Outlet />
            </EntitySchemaProvider>
          </DevicesProvider>
          <ExpiredJwtDetector />
        </ErrorBoundary>
      </DndProvider>
    </ReduxProvider>
  );
};

/**
 * Creates the browser router with all application routes.
 * Using createBrowserRouter enables data router features like useBlocker.
 */
const createAppRouter = () => {
  const publicRoutes = notLoggedInRoutes.map((route: PublicRouteConfig) => ({
    path: route.path,
    element: <route.component />,
  }));

  const privateRoutes = loggedInRoutes.map((route: RouteConfig) => ({
    path: route.path,
    element: (
      <PrivateRoute>
        <ApiIdMapper>
          <AccountChecker currentPath={route.path}>
            <PrivateRender route={route} />
          </AccountChecker>
        </ApiIdMapper>
      </PrivateRoute>
    ),
  }));

  return createBrowserRouter([
    {
      element: <RootLayout />,
      children: [...publicRoutes, ...privateRoutes, { path: '*', element: <NotFound /> }],
    },
  ]);
};

const App = () => {
  // Memoize router to prevent recreation on re-renders
  const router = useMemo(() => createAppRouter(), []);

  return (
    <>
      <RouterProvider router={router} />
      <ToastContainer position="bottom-left" theme="dark" transition={Bounce} closeOnClick={true} icon={false} />
      <AlertContainer />
      <UpdateChecker />
      <NetworkStatusDetector />
    </>
  );
};

interface PrivateRouteProps {
  children: ReactNode;
}

const PrivateRoute: FC<PrivateRouteProps> = ({ children }) => {
  const location = useLocation();
  const storageToken = window.localStorage.getItem('token');

  if (storageToken === null || storageToken === '') {
    // Redirect to login, preserving the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

interface PrivateRenderProps {
  route: RouteConfig;
}

const PrivateRender: FC<PrivateRenderProps> = ({ route }) => {
  const navigate = useNavigate();

  if (route.path === '/create-account') {
    return (
      <Authenticated globalData={false}>
        <RouteComponent component={route.component} page={route.name ?? ''} />
      </Authenticated>
    );
  }

  const isDisabled = 'disabled' in route && route.disabled === true;

  return (
    <Authenticated>
      <Intercom>
        <Layout route={route}>
          <ZendeskMessenger />
          {isDisabled ? (
            <Navigate to="/not-found" replace />
          ) : (
            <RouteComponent component={route.component} page={route.name ?? ''} history={navigate} />
          )}
        </Layout>
      </Intercom>
    </Authenticated>
  );
};

export default App;
